import { HAUPTSTATUS_WORT, type Hauptstatus } from '../../shared/guide-hauptstatus';
/**
 * OBERTHEMA -> UNTERZIEL -> BEZEICHNUNG: DIE GRUPPIERTE BUEHNENLISTE (Rafael, 24.10.2026).
 *
 * Rafael: „ich sehe eigentlich bei jeder Buehne drei Titel", „nach Oberthemen sortiert, in Gruppen",
 * „ich lese jedes Mal alle Buehnen, obwohl ich nur bei bestimmten weitermachen muss" und „ich weiss nicht,
 * was dringlicher ist und wie viel Arbeit es kostet".
 *
 * Reine Funktionen ohne React, damit Gruppierung, Zaehler „braucht dich", Sortierung und die Zeitsummen testbar
 * sind (server/__tests__/guide-oberthemen.test.ts). NICHTS wird geschaetzt oder erfunden: eine fehlende
 * Zeitschaetzung zaehlt als „offen" und wird so gezeigt; ein Zieltermin, den das Board nur als Freitext fuehrt
 * („Mitte Sep 2026"), bleibt Text und geht in keine Datumsrechnung ein.
 */

/** „Wartet auf dich" im Sinn der Liste (HOFDREI): Warteraum und die alten Werte vor_dem_koenig/vorraum. */
export const RAEUME_WARTET_AUF_DICH = ['warteraum', 'vor_dem_koenig', 'vorraum'];
export const wartetRaum = (raum: string): boolean => RAEUME_WARTET_AUF_DICH.includes(raum);

export interface OberthemaBuehne {
  hauptstatus?: Hauptstatus;
  hauptstatus_text?: string;
  arbeitsinformation?: string;
  id: string;
  titel: string;
  /** Die Fenster der Buehne. Fehlt es, ist sie noch im Bau (es gibt nichts zu oeffnen). */
  rezept?: { kacheln?: unknown[] } | null;
  thema: string;
  thema_name?: string | null;
  unterziel?: string | null;
  unterziel_name?: string | null;
  unterziel_ziel?: string | null;
  /** Die Aufloesung des Unterziels schlug fehl (Board nicht lesbar) — dann fehlt der Name, nicht das Unterziel. */
  unterziel_nicht_lesbar?: boolean;
  /** Laeuft gerade jemand an der Buehne (Server `arbeit_laeuft`)? Nur ein ausdrueckliches `false` heisst „nichts laeuft“ — fehlt das Feld, wird nichts angenommen. */
  arbeit_laeuft?: boolean;
  /**
   * Wie der SERVER den Sitzungssaal liest (`buehnenLage`, server/lib/guide-buehnen.ts): arbeitet · zuarbeit (Plan-Zuarbeit, Ruhe, externe Arbeit oder Amtswarten belegt) · gestoert
   * (niemand arbeitet gemessen). Die Zeitfenster stecken dort (`gemesseneArbeit`, `externeArbeitBelegt` …) — der Client hat KEIN eigenes Zeitmass.
   */
  saal_bereich?: 'arbeitet' | 'zuarbeit' | 'gestoert' | null;
  /** Beratermessung des Hauses fehlte (Server `ansichtMitMessung`): dann ist `arbeit_laeuft` KEINE Aussage. */
  berater_messfehler?: string;
  belegung?: string;
  /** Der Server hat die Buehne in Ruhe gelegt (z. B. „Pruefung ab …“): sie wartet auf einen Termin, nicht auf Rafael. */
  ruhend?: { grund?: string; bis?: string | null } | null;
  raum: string;
  /** Der gesetzte Raum (gespeichert); `raum` ist der gerechnete. */
  raum_gesetzt?: string;
  braucht_entscheidung?: boolean;
  /** Vollständige Beratung vom Server belegt, keine technische Phasenvermutung. */
  beratung_bereit?: boolean;
  /** Historisches Feld; erzeugt weder Anzeige noch Nutzerbedarf. */
  handgriff?: string | null;
  offen?: { entscheidungen?: number };
  arbeitsplan?: {
    /** Phase des Arbeitsplans (beauftragt … deployt) — Grundlage fuer „pausiert“ (unterbrochen) und „Ergebnis bereit“. */
    phase?: string;
    prioritaet?: 'frist' | 'entblocken' | 'ziel' | 'spaeter';
    frist?: string;
    minuten?: { min: number; max: number };
    energie?: 'leicht' | 'mittel' | 'konzentriert';
    nutzerAktion?: string;
    /** Der naechste Schritt, den der Berater eingetragen hat (Freitext). */
    naechsterSchritt?: string;
    abhaengigkeiten?: Array<{ wartetAuf: string; erledigt?: boolean }>;
  };
}

/** Hat Rafael die Buehne selbst zurueckgestellt („Auf spaeter“)? Der Pausenraum ist SEINE Entscheidung — dort wartet nichts auf ihn, auch nicht mit offener Frage (wie im alten Warteraum). */
export const imPausenraum = (b: Pick<OberthemaBuehne, 'raum' | 'raum_gesetzt'>): boolean => b.raum === 'pausenraum' || b.raum_gesetzt === 'pausenraum';

/** Nur eine echte offene Frage/Zustimmung erzeugt Nutzerbedarf. Alte Aktionsfelder,
 * Freitext-Abhaengigkeiten und ein gespeicherter Warteraum tun das nicht. */
export function brauchtDich(b: Pick<OberthemaBuehne, 'raum' | 'raum_gesetzt' | 'braucht_entscheidung' | 'offen' | 'hauptstatus'>): boolean {
  if (b.hauptstatus === 'abgeschlossen' || b.hauptstatus === 'von_dir_zurueckgestellt' || (!b.hauptstatus && imPausenraum(b))) return false;
  return b.braucht_entscheidung === true || (b.offen?.entscheidungen ?? 0) > 0;
}

/** Die noch nicht erledigten Abhaengigkeiten (`erledigt === false`; fehlt das Feld, gilt sie nicht als offen — nichts wird geraten). */
export function offeneAbhaengigkeiten(b: OberthemaBuehne): Array<{ wartetAuf: string }> {
  return (b.arbeitsplan?.abhaengigkeiten ?? []).filter(a => a.erledigt === false);
}
/** Entfernt die Sitzungskennung vorn („session:91624155-… Zustellung in …“): fuer Rafael zaehlt der Wortlaut dahinter. */
export const abhaengigkeitWort = (wartetAuf: string): string => wartetAuf.replace(/^session:[0-9a-f-]{8,}\s*/i, '').trim();
const zeit2 = (n: number) => String(n).padStart(2, '0');
/** „Prüfung ab 25.10. 10:00“ aus `ruhend.bis` (Ortszeit); ein anderer Grund steht mit seinem Namen da — nichts wird erfunden. */
export function ruheWort(r: { grund?: string; bis?: string | null }): string {
  const t = r.bis ? Date.parse(r.bis) : NaN;
  const d = Number.isNaN(t) ? null : new Date(t);
  const wann = d ? `${zeit2(d.getDate())}.${zeit2(d.getMonth() + 1)}. ${zeit2(d.getHours())}:${zeit2(d.getMinutes())}` : null;
  if (r.grund === 'pruefen_ab') return wann ? `Prüfung ab ${wann}` : 'Prüfung steht an';
  if (r.grund === 'zuarbeit_lebt') return 'Zuarbeit läuft';
  return `ruht (${r.grund ?? 'ohne Grund'})${wann ? ` bis ${wann}` : ''}`;
}
/** Worauf eine Buehne im Status „wartet auf …“ wartet: die offenen Abhaengigkeiten (Wortlaut), sonst die Ruhe. `null`, wenn es beides nicht gibt. */
export function anderesGrund(b: OberthemaBuehne, max = 140): { text: string; mehr: number } | null {
  const deps = offeneAbhaengigkeiten(b);
  if (deps.length) return { text: ersteSatzgruppe(abhaengigkeitWort(deps[0].wartetAuf), max), mehr: deps.length - 1 };
  if (b.ruhend) return { text: ruheWort(b.ruhend), mehr: 0 };
  return null;
}

/** Kleiner Rang = dringlicher. Ohne Prioritaet = ungeplant (hinten, nicht „niedrig"). */
export function dringlichkeitsRang(b: OberthemaBuehne): number {
  switch (b.arbeitsplan?.prioritaet) {
    case 'frist': return 0;
    case 'entblocken': return 1;
    case 'ziel': return 2;
    case 'spaeter': return 3;
    default: return 4;
  }
}

export const PRIORITAET_WORT: Record<string, string> = {
  frist: 'Frist', entblocken: 'entblockt Arbeit', ziel: 'Zielarbeit', spaeter: 'später',
};

/** „31.10.2026" oder „2026-10-31" -> „2026-10-31"; alles andere (Freitext) -> null. Es wird nie geraten. */
export function zielDatum(text: string | null | undefined): string | null {
  if (!text) return null;
  const t = text.trim();
  const de = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(t);
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  const [j, m, d] = de ? [de[3], de[2], de[1]] : iso ? [iso[1], iso[2], iso[3]] : [null, null, null];
  if (!j || !m || !d) return null;
  const probe = new Date(Date.UTC(+j, +m - 1, +d));
  if (probe.getUTCFullYear() !== +j || probe.getUTCMonth() !== +m - 1 || probe.getUTCDate() !== +d) return null;
  return `${j}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
}

/** Ganze Tage von `jetzt` bis zum Zieltag (Wiener Kalendertag); negativ = ueberfaellig. */
export function tageBis(zielIso: string, jetzt: number): number {
  const heute = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Vienna', year: 'numeric', month: '2-digit', day: '2-digit' }).format(jetzt);
  return Math.round((Date.parse(`${zielIso}T00:00:00Z`) - Date.parse(`${heute}T00:00:00Z`)) / 86_400_000);
}

export const datumDe = (iso: string): string => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;

export interface Zeitsumme {
  /** Summe der geschaetzten Minuten (nur wo gesetzt). */
  min: number; max: number;
  /** Buehnen mit Schaetzung / ohne (= „Aufwand offen"). */
  mit: number; offen: number;
}

export function zeitsumme(bs: OberthemaBuehne[]): Zeitsumme {
  const s: Zeitsumme = { min: 0, max: 0, mit: 0, offen: 0 };
  for (const b of bs) {
    const m = b.arbeitsplan?.minuten;
    if (m) { s.min += m.min; s.max += m.max; s.mit++; } else s.offen++;
  }
  return s;
}

export interface Zielstand {
  /** Spaetestes lesbares Zieldatum (ISO) der Gruppe, sonst null. */
  spaetestes: string | null;
  /** Unterziele, deren Termin im Board nur als Freitext oder gar nicht steht. */
  ohneDatum: number;
}

export interface Unterzielgruppe<T extends OberthemaBuehne = OberthemaBuehne> {
  schluessel: string;
  /** Anzeigename; „ohne Unterziel" fuer Bestand. */
  name: string;
  zielText: string | null;
  zielIso: string | null;
  buehnen: T[];
  braucht: number;
}

export interface Oberthemagruppe<T extends OberthemaBuehne = OberthemaBuehne> {
  schluessel: string;
  name: string;
  /** Die Sammelgruppe „Divers · noch einzuordnen“: alles ohne Board-Oberthema mit lesbarem Unterziel. */
  divers: boolean;
  /** Buehnen im Status „kein naechster Schritt“ — Aufmerksamkeit, nicht „braucht dich“ (Rafael 25.10.2026: der Berater fuehrt bis zum Abschluss). */
  ohneSchritt: number;
  /** Falsch, wenn `thema` Freitext ist und kein Board-Thema (dann steht es sichtbar als solches da). */
  imBoard: boolean;
  unterziele: Unterzielgruppe<T>[];
  anzahl: number;
  braucht: number;
  /** Buehnen in einer Stoerung (gestoert / ohne Berater): zaehlen NICHT als „braucht dich“, bleiben aber im Kopf sichtbar. */
  stoerungen: number;
  zeit: Zeitsumme;
  ziel: Zielstand;
}

const OHNE_UNTERZIEL = '__ohne_unterziel__';

/**
 * DIVERS (Rafael, 24.10.2026: „wenn ich eine neue Bühne anlege, ordnet sie sich dann selber zu? … sonst einfach unter Divers“): alles, was keinem Board-Oberthema mit lesbarem
 * Unterziel zugeordnet ist, steht in EINER Gruppe statt verstreut als „Mail (nicht im Board)“, „FFG (nicht im Board)“ und „ohne Unterziel“. Rein lesend — keine Daten
 * werden geaendert; die urspruengliche Ursache bleibt an der Zeile sichtbar (`zuordnungsGrund`).
 */
export const DIVERS_SCHLUESSEL = '__divers__';
export const DIVERS_NAME = 'Divers · noch einzuordnen';
export const DIVERS_ERKLAERUNG = 'Neu angelegte Bühnen ohne Ziel landen hier, bis sie zugeordnet sind.';

/** Warum eine Buehne unter „Divers“ steht — `null`, wenn sie ein Board-Oberthema mit lesbarem Unterziel hat. */
export function zuordnungsGrund(b: OberthemaBuehne): string | null {
  if (!b.thema_name) return `Thema: ${b.thema || 'keines'}, kein Board-Thema`;
  if (!b.unterziel) return 'kein Unterziel';
  if (b.unterziel_nicht_lesbar) return 'Unterziel nicht lesbar';
  if (!b.unterziel_name) return `Unterziel ${b.unterziel} im gewählten Board-Thema nicht gefunden`;
  return null;
}

export function gruppiere<T extends OberthemaBuehne>(buehnen: T[], istStoerung: (b: T) => boolean = () => false): Oberthemagruppe<T>[] {
  const nachThema = new Map<string, T[]>();
  const divers: T[] = [];
  for (const b of buehnen) {
    if (zuordnungsGrund(b) !== null) { divers.push(b); continue; }
    nachThema.set(b.thema, [...(nachThema.get(b.thema) ?? []), b]);
  }

  const gruppen: Oberthemagruppe<T>[] = [...nachThema.entries()].map(([thema, bs]) => {
    const nachUnterziel = new Map<string, T[]>();
    for (const b of bs) {
      const k = b.unterziel || OHNE_UNTERZIEL;
      nachUnterziel.set(k, [...(nachUnterziel.get(k) ?? []), b]);
    }
    const unterziele: Unterzielgruppe<T>[] = [...nachUnterziel.entries()].map(([k, ub]) => {
      const ohne = k === OHNE_UNTERZIEL;
      const text = ohne ? null : (ub[0].unterziel_ziel ?? null);
      return {
        schluessel: `${thema}::${k}`,
        name: ohne ? 'ohne Unterziel'
          : ub[0].unterziel_nicht_lesbar ? `${k} (Unterziel nicht lesbar)`
          : (ub[0].unterziel_name ?? `${k} (im gewählten Board-Thema nicht gefunden)`),
        zielText: text, zielIso: zielDatum(text),
        buehnen: [...ub].sort(vergleicheInGruppe),
        braucht: ub.filter(brauchtDich).length,
      };
    }).sort((a, b) => Number(a.name === 'ohne Unterziel') - Number(b.name === 'ohne Unterziel')
      || b.braucht - a.braucht
      || (a.zielIso ?? '9999').localeCompare(b.zielIso ?? '9999')
      || a.name.localeCompare(b.name, 'de'));

    const echte = unterziele.filter(u => u.name !== 'ohne Unterziel');
    const daten = echte.map(u => u.zielIso).filter((x): x is string => !!x);
    return {
      schluessel: thema,
      name: bs[0].thema_name || thema,
      divers: false,
      ohneSchritt: bs.filter(x => statusVon(x) === 'ohneSchritt').length,
      imBoard: !!bs[0].thema_name,
      unterziele,
      anzahl: bs.length,
      braucht: bs.filter(brauchtDich).length,
      stoerungen: bs.filter(istStoerung).length,
      zeit: zeitsumme(bs),
      ziel: { spaetestes: daten.length ? daten.sort()[daten.length - 1] : null, ohneDatum: echte.length - daten.length },
    };
  });

  // Gruppen mit Buehnen, die Rafael brauchen, zuerst (mehr Bedarf zuerst); danach nach Namen — stabil, kein Springen. „Divers“ steht IMMER ganz hinten.
  const sortiert = gruppen.sort((a, b) => Number(b.braucht > 0) - Number(a.braucht > 0)
    || b.braucht - a.braucht
    || a.name.localeCompare(b.name, 'de'));
  if (!divers.length) return sortiert;
  const sortDivers = [...divers].sort(vergleicheInGruppe);
  return [...sortiert, {
    schluessel: DIVERS_SCHLUESSEL, name: DIVERS_NAME, divers: true, imBoard: true, ohneSchritt: divers.filter(x => statusVon(x) === 'ohneSchritt').length,
    unterziele: [{ schluessel: `${DIVERS_SCHLUESSEL}::alle`, name: 'ohne Unterziel', zielText: null, zielIso: null, buehnen: sortDivers, braucht: sortDivers.filter(brauchtDich).length }],
    anzahl: divers.length, braucht: divers.filter(brauchtDich).length, stoerungen: divers.filter(istStoerung).length, zeit: zeitsumme(divers),
    ziel: { spaetestes: null, ohneDatum: 0 },
  }];
}

/** Innerhalb einer Gruppe: wer Rafael braucht zuerst, dann dringlicher, dann frueherer Termin, dann Titel. */
function vergleicheInGruppe(a: OberthemaBuehne, b: OberthemaBuehne): number {
  return Number(brauchtDich(b)) - Number(brauchtDich(a))
    || dringlichkeitsRang(a) - dringlichkeitsRang(b)
    || (a.arbeitsplan?.frist ?? '9999').localeCompare(b.arbeitsplan?.frist ?? '9999')
    || a.titel.localeCompare(b.titel, 'de');
}

/** „ca. 20–40 Min." oder „Aufwand offen" — nie eine Zahl, die niemand geschaetzt hat. */
export function aufwandWort(b: OberthemaBuehne): string {
  const m = b.arbeitsplan?.minuten;
  if (!m) return 'Aufwand offen';
  return m.min === m.max ? `ca. ${m.min} Min.` : `ca. ${m.min}–${m.max} Min.`;
}

export function zeitsummeWort(z: Zeitsumme): string {
  if (z.mit === 0) return `Aufwand offen (${z.offen})`;
  const summe = z.min === z.max ? `${z.min} Min.` : `${z.min}–${z.max} Min.`;
  return `Σ ${summe}${z.offen ? ` · bei ${z.offen} offen` : ''}`;
}

export function zielstandWort(z: Zielstand, jetzt: number): string {
  if (!z.spaetestes) return z.ohneDatum ? `Ziel: kein lesbares Datum (${z.ohneDatum})` : 'Ziel: offen';
  const t = tageBis(z.spaetestes, jetzt);
  const rest = t < 0 ? `${-t} Tage überfällig` : t === 0 ? 'heute' : `in ${t} Tagen`;
  return `Ziel spätestens ${datumDe(z.spaetestes)} (${rest})${z.ohneDatum ? ` · ${z.ohneDatum} ohne lesbares Datum` : ''}`;
}

/** ISO-Zeit einer Frist -> „TT.MM.JJJJ" nach Wiener Kalendertag; ungueltig -> null (kein Raten). */
export function fristDatum(iso: string | undefined): string | null {
  if (!iso || Number.isNaN(Date.parse(iso))) return null;
  const tag = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Vienna', year: 'numeric', month: '2-digit', day: '2-digit' }).format(Date.parse(iso));
  return datumDe(tag);
}

// ── Tabs: Oberthema und Unterziel waehlen (nur Zustand der Ansicht, keine Datenlogik) ─────────────────────────────
/** `null` = „Alle“. Die Auswahl lebt nur im React-State, nie im Browserspeicher (das L0-Gate wertet das als stillen Rueckfall). */
export interface Auswahl { gruppe: string | null; unterziel: string | null }
export const ALLE_AUSWAHL: Auswahl = { gruppe: null, unterziel: null };

export interface WirksameAuswahl {
  auswahl: Auswahl;
  /** Das gewaehlte Oberthema gibt es nicht mehr: es steht wieder „Alle“, und die Ansicht SAGT das. */
  zurueckAufAlle: boolean;
  /** Das gewaehlte Unterziel gibt es in diesem Oberthema nicht mehr: es steht wieder „Alle Unterziele“. */
  unterzielVerloren: boolean;
}

/** Bringt die gemerkte Auswahl auf den aktuellen Bestand. Nichts wird still verworfen: der Aufrufer zeigt die beiden Flags. */
export function wirksameAuswahl<T extends OberthemaBuehne>(gruppen: Oberthemagruppe<T>[], a: Auswahl): WirksameAuswahl {
  if (a.gruppe === null) return { auswahl: ALLE_AUSWAHL, zurueckAufAlle: false, unterzielVerloren: false };
  const g = gruppen.find(x => x.schluessel === a.gruppe);
  if (!g) return { auswahl: ALLE_AUSWAHL, zurueckAufAlle: true, unterzielVerloren: false };
  if (a.unterziel !== null && !g.unterziele.some(u => u.schluessel === a.unterziel)) {
    return { auswahl: { gruppe: a.gruppe, unterziel: null }, zurueckAufAlle: false, unterzielVerloren: true };
  }
  return { auswahl: a, zurueckAufAlle: false, unterzielVerloren: false };
}

/** „Alle“: alle Gruppen unveraendert. Ein Oberthema: nur diese Gruppe, bei gewaehltem Unterziel nur dessen Zeile(n). */
export function sichtbareGruppen<T extends OberthemaBuehne>(gruppen: Oberthemagruppe<T>[], a: Auswahl): Oberthemagruppe<T>[] {
  if (a.gruppe === null) return gruppen;
  const g = gruppen.find(x => x.schluessel === a.gruppe);
  if (!g) return gruppen;
  return [a.unterziel === null ? g : { ...g, unterziele: g.unterziele.filter(u => u.schluessel === a.unterziel) }];
}

/** „E3 Energy: Kosten je Bericht in jeder Preisstufe gedeckt“ -> Kuerzel „E3“ + Kurzname „Energy: Kosten je Bericht in jeder…“. */
export function unterzielKurz(name: string, max = 30): { kuerzel: string | null; kurz: string } {
  const m = /^([A-Z]\d+)\s+(.+)$/.exec(name.trim());
  const rest = (m ? m[2] : name).trim();
  return { kuerzel: m ? m[1] : null, kurz: rest.length > max ? `${rest.slice(0, max - 1).trimEnd()}…` : rest };
}

/** Pfeiltasten in einer Tab-Leiste (WAI-ARIA): rechts/links mit Umbruch, Pos1/Ende. `null` = Taste ist nicht gemeint. */
export function tabNachTaste(aktuell: number, anzahl: number, taste: string): number | null {
  if (anzahl <= 0) return null;
  switch (taste) {
    case 'ArrowRight': return (aktuell + 1) % anzahl;
    case 'ArrowLeft': return (aktuell - 1 + anzahl) % anzahl;
    case 'Home': return 0;
    case 'End': return anzahl - 1;
    default: return null;
  }
}

// ── Warteraum nach Zielen (Rafael, 24.10.2026: „auch ganz oben der Warteraum soll nach Zielen geordnet werden koennen") ─────────
/** Reihenfolge im Block „Wartet auf dich“: wie bisher nach Dringlichkeit (Vorgabe) oder gruppiert nach Oberthema und Unterziel. Nur Zustand der Ansicht. */
export type WartReihenfolge = 'dringlichkeit' | 'ziele';

/**
 * Gruppiert die WARTENDEN Buehnen nach Oberthema -> Unterziel. Die OBERTHEMEN stehen in derselben Reihenfolge wie die Tabs der Liste
 * (dort wird ueber ALLE lebenden Buehnen gruppiert), mit denselben Namen. Die UNTERZIELE jeder Gruppe stehen in der Ordnung der Tabs-
 * Ansicht desselben Oberthemas (Bedarf, dann Zieltermin, dann Name; „ohne Unterziel“ zuletzt). Innerhalb eines Unterziels bleibt die
 * Reihenfolge der uebergebenen Liste (Frage zuerst, Vorlagerang) erhalten. Buehnen ohne Unterziel stehen sichtbar unter „ohne Unterziel“.
 */
export function wartendeNachZielen<T extends OberthemaBuehne>(wartende: T[], alle: T[], istStoerung: (b: T) => boolean = () => false): Oberthemagruppe<T>[] {
  const reihenfolge = gruppiere(alle, istStoerung).map(g => g.schluessel);
  const gruppenRang = (k: string): number => { const i = reihenfolge.indexOf(k); return i === -1 ? Number.MAX_SAFE_INTEGER : i; };
  const unterzielRang = new Map(gruppiere(alle, istStoerung).flatMap(g => g.unterziele.map((u, i) => [u.schluessel, i] as const)));
  const urspruenglich = new Map(wartende.map((b, i) => [b.id, i]));
  return gruppiere(wartende, istStoerung)
    .map(g => ({ ...g, unterziele: g.unterziele
      .map(u => ({ ...u, buehnen: [...u.buehnen].sort((a, b) => (urspruenglich.get(a.id) ?? 0) - (urspruenglich.get(b.id) ?? 0)) }))
      .sort((a, b) => (unterzielRang.get(a.schluessel) ?? Number.MAX_SAFE_INTEGER) - (unterzielRang.get(b.schluessel) ?? Number.MAX_SAFE_INTEGER)) }))
    .sort((a, b) => gruppenRang(a.schluessel) - gruppenRang(b.schluessel) || a.name.localeCompare(b.name, 'de'));
}

// ── Kachelübersicht „Alle“ (Rafael, 24.10.2026: „das ist aber endlos zu scrollen“) ──────────────────────────────────────
/** Der naechste Termin einer Gruppe: die frueheste Buehnen-Frist, sonst das fruehste lesbare Zieldatum eines Unterziels. Ueberfaellig zaehlt (steht vorn). */
export function naechsterTermin<T extends OberthemaBuehne>(g: Oberthemagruppe<T>): { art: 'frist' | 'ziel'; text: string } | null {
  const fristen = g.unterziele.flatMap(u => u.buehnen).map(b => b.arbeitsplan?.frist).filter((f): f is string => !!f && !Number.isNaN(Date.parse(f)));
  if (fristen.length) {
    const frueh = fristen.sort((a, b) => Date.parse(a) - Date.parse(b))[0];
    return { art: 'frist', text: (fristDatum(frueh) ?? '').slice(0, 6) };
  }
  const ziele = g.unterziele.map(u => u.zielIso).filter((z): z is string => !!z).sort();
  return ziele.length ? { art: 'ziel', text: datumDe(ziele[0]).slice(0, 6) } : null;
}

/** Die dringendsten Buehnen, die Rafael brauchen (hoechstens `n`): Prioritaet, dann Frist, dann Titel. */
export function dringendsteBedarf<T extends OberthemaBuehne>(g: Oberthemagruppe<T>, n = 2): T[] {
  return g.unterziele.flatMap(u => u.buehnen).filter(brauchtDich)
    .sort((a, b) => dringlichkeitsRang(a) - dringlichkeitsRang(b)
      || (a.arbeitsplan?.frist ?? '9999').localeCompare(b.arbeitsplan?.frist ?? '9999')
      || a.titel.localeCompare(b.titel, 'de'))
    .slice(0, n);
}

/** „Σ 60–100 Min.“ — nur wo es Schaetzungen gibt; sonst null (kein „Aufwand offen“-Rauschen in der Kachel). */
export function zeitsummeKurz(z: Zeitsumme): string | null {
  if (z.mit === 0) return null;
  return z.min === z.max ? `Σ ${z.min} Min.` : `Σ ${z.min}–${z.max} Min.`;
}

// ── Inhalte der kompakten Karte/Zeile bleiben erreichbar (Zweitpruefung 24.10.2026: Tooltips reichen auf Touch nicht) ─────────────
export interface InfoZeile { /** Kennung fuer data-ai-id (z. B. guide-alter-<id>); ohne Angabe nur Text. */ ai?: string; text: string }

export interface KompaktInfoQuelle {
  id: string;
  stand?: string | null;
  arbeitsplan?: { naechsterSchritt?: string; minuten?: { min: number; max: number }; rueckmeldungAm?: string } | null;
  vorgezogen?: { von: string; am: string; grund: string } | null;
  ungepflegtSeit?: string | null;
  raumgrund?: { am: string; grund: string } | null;
  arbeitende?: string | null;
  stufe?: number | null;
  alter?: string | null;
}

/**
 * Was die kompakte Karte/Zeile NICHT mehr als eigene Zeile zeigt, steht hier — als Zeilen fuer das ⓘ-Feld (Klick/Tap/Tastatur) und den Tooltip.
 * Fehlende Teile fehlen; nichts wird erfunden. `uhr` formatiert eine ISO-Zeit, `jetzt` entscheidet, ob eine Rueckmeldung ueberfaellig ist.
 */
export function kompaktInfoZeilen(q: KompaktInfoQuelle, f: { uhr: (iso: string) => string | null; jetzt: number }): InfoZeile[] {
  const z: InfoZeile[] = [];
  if (q.stand) z.push({ text: q.stand });
  const p = q.arbeitsplan;
  if (p && (p.naechsterSchritt || p.minuten)) {
    const teile = [p.naechsterSchritt ? `Nächster Schritt: ${p.naechsterSchritt}` : null, p.minuten ? `ca. ${p.minuten.min}–${p.minuten.max} Min.` : null,
      p.rueckmeldungAm && Date.parse(p.rueckmeldungAm) < f.jetzt ? 'Rückmeldung überfällig' : null].filter(Boolean);
    z.push({ ai: 'guide-arbeitsplan', text: teile.join(' · ') });
  }
  if (q.vorgezogen) z.push({ text: `vorgezogen von ${q.vorgezogen.von} (${f.uhr(q.vorgezogen.am) ?? q.vorgezogen.am}) — ${q.vorgezogen.grund}` });
  if (q.ungepflegtSeit) z.push({ text: `Bühnenmodus aus seit ${f.uhr(q.ungepflegtSeit) ?? q.ungepflegtSeit} — keine Pflicht mehr, bleibt aber stehen` });
  if (q.raumgrund) z.push({ text: `Assistent · ${f.uhr(q.raumgrund.am) ?? q.raumgrund.am}: ${q.raumgrund.grund}` });
  if (q.arbeitende) z.push({ ai: `guide-arbeitende-${q.id}`, text: q.arbeitende });
  const alter = [q.stufe !== null && q.stufe !== undefined ? `Stufe ${q.stufe}` : null, q.alter ? `seit ${q.alter}` : null].filter(Boolean);
  if (alter.length) z.push({ ai: `guide-alter-${q.id}`, text: alter.join(' · ') });
  return z;
}

// ── Warteraum nach Zielen: Begrenzung NACH dem Gruppieren (keine Gruppe geht verloren) ────────────────────────────────────────
export type BegrenzteGruppe<T extends OberthemaBuehne> = Oberthemagruppe<T> & { ausgeblendet: number };

/**
 * Zeigt je Gruppe hoechstens `max` Buehnen (ueber ihre Unterziele hinweg, in der Reihenfolge der Unterziele). JEDE Gruppe bleibt erhalten,
 * auch wenn sie nur ausgeblendete Buehnen haette — sonst fehlten Gruppen, die auf Rafael warten. Unterziele ohne sichtbare Buehne entfallen.
 * `anzahl` und `braucht` bleiben die der ganzen Gruppe; `ausgeblendet` sagt, wie viele fehlen.
 */
export function begrenzeGruppen<T extends OberthemaBuehne>(gruppen: Oberthemagruppe<T>[], max: number): BegrenzteGruppe<T>[] {
  return gruppen.map(g => {
    let rest = max;
    const unterziele = g.unterziele.map(u => {
      const sichtbar = u.buehnen.slice(0, Math.max(0, rest));
      rest -= sichtbar.length;
      return { ...u, buehnen: sichtbar };
    }).filter(u => u.buehnen.length > 0);
    const gezeigt = unterziele.reduce((n, u) => n + u.buehnen.length, 0);
    return { ...g, unterziele, ausgeblendet: g.anzahl - gezeigt };
  });
}

// ── Umschalter „Räume (bisher) | Ziele (neu)“, Vorgabe je Nutzerart, Hinweis „Was ist neu und warum“, Stern ────────────────────
/** „raum“ = Räume (bisher), die unveränderte Raumansicht; „oberthema“ = Ziele (neu). */
export type Ansicht = 'oberthema' | 'raum';

/**
 * VORGABE (Rafael, 24.10.2026: „standardmaessig soll es fuer alle im alten Modus bleiben ausser bei mir"): „Ziele (neu)“ nur fuer den Betreiber
 * (Rafael: Anmeldung aus oder Rolle admin — dieselbe Erkennung wie `istBetreiberHier`); Partner bleiben bei „Räume (bisher)“.
 */
export const standardAnsicht = (istBetreiber: boolean): Ansicht => (istBetreiber ? 'oberthema' : 'raum');
/** Die gewaehlte Ansicht der geoeffneten Sitzung, sonst die Vorgabe. */
export const wirksameAnsicht = (wahl: Ansicht | null, istBetreiber: boolean): Ansicht => wahl ?? standardAnsicht(istBetreiber);

/** Zustand des Hinweises. `zieleGeoeffnet`: die Ziele-Ansicht war einmal offen; `geschlossen`: „Verstanden“ wurde gedrueckt. */
export interface AnsichtsHinweis { offen: boolean; geschlossen: boolean; zieleGeoeffnet: boolean }
/** Beim ersten Anzeigen in der Sitzung ist der Hinweis nur fuer den Betreiber offen; Partner sehen zunaechst nur den Stern. */
export const hinweisAnfang = (istBetreiber: boolean): AnsichtsHinweis => ({ offen: istBetreiber, geschlossen: false, zieleGeoeffnet: istBetreiber });

export type HinweisEreignis = 'ziele-geoeffnet' | 'stern' | 'info' | 'verstanden' | 'ziele-ansehen';
/**
 * EINE Erklaerung statt zwei (Rafael, 24.10.2026): beim PARTNER ist die Karte „So funktioniert dein Guide“ die Erklaerung — Stern und ⓘ oeffnen sie, das Feld „Was ist
 * neu und warum“ gibt es dort nie. Nur der Betreiber (Rafael) hat das Feld, und keine Karte. Daher stehen nie zwei Erklaerungen gleichzeitig offen.
 */
export function hinweisZiel(e: HinweisEreignis, istBetreiber: boolean): 'feld' | 'karte' | 'zustand' {
  if (e === 'stern' || e === 'info') return istBetreiber ? 'feld' : 'karte';
  return 'zustand';
}
export function hinweisNach(z: AnsichtsHinweis, e: HinweisEreignis): AnsichtsHinweis {
  switch (e) {
    case 'ziele-geoeffnet': return { ...z, zieleGeoeffnet: true };
    case 'stern': return { ...z, offen: true };
    case 'info': return { ...z, offen: !z.offen };
    case 'verstanden': return { ...z, offen: false, geschlossen: true };
    case 'ziele-ansehen': return { ...z, offen: false, zieleGeoeffnet: true };
  }
}
/** Der Stern „NEU“ steht bei jedem Nutzer, bis er die Ziele-Ansicht einmal geoeffnet oder den Hinweis geschlossen hat. Umschalter und ⓘ bleiben immer. */
export const sternSichtbar = (z: AnsichtsHinweis): boolean => !z.zieleGeoeffnet && !z.geschlossen;

/**
 * Der Zustand der geoeffneten Ansicht liegt im Speicher des Skripts (kein Server). Ueber ein Neuladen hinaus merkt das Fenster nur `hinweisGesehen` und die bewusst
 * gewaehlte Ansicht, ueber `useUiPreferences` (lib/useGuideAnsichtSitzung); ohne Gemerktes beginnt es wieder bei der Vorgabe.
 * Je Nutzerkennung, damit ein Wechsel des Nutzers ohne Neuladen nichts vom Vorgaenger erbt.
 */
export interface AnsichtSitzung { nutzer: string; wahl: Ansicht | null; hinweis: AnsichtsHinweis }
let gemerkteSitzung: AnsichtSitzung | null = null;
/** Der Hinweis nach „gesehen“: nicht offen, kein Stern; das ⓘ ruft ihn weiter ab. */
export const hinweisGesehenZustand: AnsichtsHinweis = { offen: false, geschlossen: true, zieleGeoeffnet: true };
/**
 * Der Anfang einer Sitzung dieses Nutzers: aus dem, was das Fenster fuer ihn GEMERKT hat (`gemerkt`, siehe lib/guide-ansicht-merk), sonst aus seiner Nutzerart.
 * Eine bewusst gewaehlte Ansicht schlaegt die Vorgabe; ein gesehener Hinweis erscheint nicht von selbst.
 */
export function ansichtSitzung(nutzer: string, istBetreiber: boolean, gemerkt?: { hinweisGesehen: boolean; wahl: Ansicht | null }): AnsichtSitzung {
  if (!gemerkteSitzung || gemerkteSitzung.nutzer !== nutzer) {
    gemerkteSitzung = { nutzer, wahl: gemerkt?.wahl ?? null, hinweis: gemerkt?.hinweisGesehen ? hinweisGesehenZustand : hinweisAnfang(istBetreiber) };
  }
  return gemerkteSitzung;
}
export function merkeAnsichtSitzung(s: AnsichtSitzung): void { gemerkteSitzung = s; }
/**
 * Wechselt die Nutzerkennung bei laufender Ansicht (Abmelden/Anmelden ohne Neumounten), gehoert der gehaltene Zustand (Ansicht, Hinweis, Stern) dem Vorgaenger:
 * er wird neu aus der Nutzerart des Neuen abgeleitet. Gleiche Kennung: derselbe Zustand.
 */
export function sitzungFuerNutzer(aktuell: AnsichtSitzung, nutzer: string, istBetreiber: boolean, gemerkt?: { hinweisGesehen: boolean; wahl: Ansicht | null }): AnsichtSitzung {
  return aktuell.nutzer === nutzer ? aktuell : ansichtSitzung(nutzer, istBetreiber, gemerkt);
}
/** Nur fuer Tests: vergisst die gemerkte Sitzung (entspricht einem Neuladen der Seite). */
export function vergesseAnsichtSitzung(): void { gemerkteSitzung = null; }

// ── Räume sind jetzt Filter (Rafael, 24.10.2026: „eigentlich ist das ja nur eine Art Filter“) ───────────────────────────────────
/** Der Status einer Buehne in der Ziele-Ansicht. Jede Buehne hat genau einen (die Zaehler ergeben zusammen „Alle“). */
export type Status = 'wartet' | 'anderes' | 'arbeitet' | 'still' | 'unbekannt' | 'pausiert' | 'ergebnis' | 'ohneSchritt' | 'imBau' | 'abgeschlossen';
// Legacy filter keys remain readable, but they map to the four product states.
export const STATUS_WORT: Record<Status, string> = {
  wartet: HAUPTSTATUS_WORT.fuer_dich_vorbereitet, arbeitet: HAUPTSTATUS_WORT.in_arbeit,
  pausiert: HAUPTSTATUS_WORT.von_dir_zurueckgestellt, abgeschlossen: HAUPTSTATUS_WORT.abgeschlossen,
  anderes: HAUPTSTATUS_WORT.in_arbeit, still: HAUPTSTATUS_WORT.in_arbeit,
  unbekannt: HAUPTSTATUS_WORT.in_arbeit, ohneSchritt: HAUPTSTATUS_WORT.in_arbeit,
  imBau: HAUPTSTATUS_WORT.in_arbeit, ergebnis: HAUPTSTATUS_WORT.fuer_dich_vorbereitet,
};
export const STATUS_SYMBOL: Record<Status, string> = { wartet:'◉', arbeitet:'▶', pausiert:'⏸', abgeschlossen:'✓', anderes:'▶', still:'▶', unbekannt:'▶', ohneSchritt:'▶', imBau:'▶', ergebnis:'◉' };
export const STATUS_FILTER_WORT = STATUS_WORT;
export const STATUS_REIHENFOLGE: readonly Status[] = ['arbeitet', 'wartet', 'pausiert', 'abgeschlossen'];
/**
 * Welche Marken die Filterleiste zeigt: „Wartet auf dich“ immer, die uebrigen nur mit Treffer oder wenn sie gewaehlt sind. Sechs Marken samt Alle, ⓘ und „Räume (bisher)“ passten
 * in 740 px nicht mehr in eine Zeile; eine Marke mit 0 filtert nichts.
 */
export function sichtbareStatus(zaehler: Record<Status, number>, filter: Status | null): Status[] {
  return [...STATUS_REIHENFOLGE];
}

/** Persönliche Fragen, gemessene Arbeit und offene Zuarbeit gehen vor.
 * Nur der Serverbeleg einer vollständigen Beratung trägt die neutrale Beratungsmarke. */
/** Nennt eine offene Abhaengigkeit die Zustellung des Ergebnisses? (Dann wartet ein fertiges Ergebnis noch auf etwas anderes.) */
export const wartetAufZustellung = (b: OberthemaBuehne): boolean => offeneAbhaengigkeiten(b).some(a => /zustell/i.test(a.wartetAuf));
/** Die Zeile zu „kein naechster Schritt“: behauptet „nicht eingetragen“ NUR, wenn `naechsterSchritt` wirklich fehlt — sonst steht der vorhandene Wortlaut da. */
export function ohneSchrittZeile(b: OberthemaBuehne, max = 90): { zeile: string; voll: string } {
  const n = b.arbeitsplan?.naechsterSchritt?.trim();
  if (!n) return { zeile: 'Berater hat keinen nächsten Schritt eingetragen', voll: 'Kein nächster Schritt — gerade ohne laufende Arbeit, es gibt keine offene Abhängigkeit und keinen Prüftermin; im Arbeitsplan steht kein nächster Schritt' };
  return { zeile: `Gerade ohne laufende Arbeit — nächster Schritt steht noch aus: „${n.length > max ? `${n.slice(0, max - 1)}…` : n}“`, voll: `Kein nächster Schritt in Arbeit — gerade ohne laufende Arbeit, keine offene Abhängigkeit, kein Prüftermin; im Arbeitsplan steht: „${n}“` };
}
/**
 * „Ergebnis bereit“ MIT Ruhe oder offener Abhaengigkeit (z. B. „Pruefung ab 27.10. 10:25“): das Ergebnis steht, aber der Berater prueft noch bzw. etwas ist offen — der Wortlaut
 * steht in der Zeile, damit „bereit“ nichts verschweigt. `null` bei einem Ergebnis ohne beides (oder einer Buehne, die gar keines hat).
 */
export function ergebnisZusatz(b: OberthemaBuehne, max = 140): { zeile: string; voll: string } | null {
  if (statusVon(b) !== 'ergebnis') return null;
  const kurz = anderesGrund(b, max); const voll = anderesGrund(b, 600);
  if (!kurz || !voll) return null;
  const alle = offeneAbhaengigkeiten(b).map(a => abhaengigkeitWort(a.wartetAuf));
  return { zeile: `Ergebnis liegt vor — ${kurz.text}${kurz.mehr > 0 ? ` (+${kurz.mehr})` : ''}`, voll: `Ergebnis bereit — ${alle.length ? alle.join(' · ') : voll.text}${alle.length && b.ruhend ? ` · ${ruheWort(b.ruhend)}` : ''}` };
}
/** Vollständige Beratung laut gemeinsamer Serverprüfung; technische Phasen allein zählen nicht. */
export function ergebnisDa(b: OberthemaBuehne): boolean {
  return b.beratung_bereit === true;
}
/*
 * TAETIGKEIT VOR ERGEBNIS (Rafael 25.10.2026: „schau dass … ich auch sehe ob die grad was macht oder nicht“;
 * Anlass: die Businessplan-Buehne zeigte „Ergebnis“ und den Pausenknopf, waehrend am Deck Code gebaut wurde).
 * „arbeitet“ nur mit gemessener Arbeit (`arbeit_laeuft === true`), auch wenn schon ein Ergebnis da ist — das
 * steht dann daneben (`ergebnisDa`). Fehlt die Messung des Hauses (`berater_messfehler`/`belegung: unbekannt`)
 * oder das Feld ganz, heisst es „unbekannt“, nicht „arbeitet“. Gemessen nichts laufend und nichts anderes
 * zutreffend: „still“ (kurze Luecke zwischen zwei Zuegen; kein Alarm, aber auch kein erfundenes Arbeiten —
 * „ruht“ hat Rafael am 03.10.2026 abgeschafft). Belegte Zuarbeit (`saal_bereich: 'zuarbeit'`, extern bzw. Amt)
 * ist „wartet auf anderes“ mit Grund.
 */
/** Hat die Buehne Fenster? Ohne Rezept ist sie noch im Bau (Befund Vize Partner 31.10.2026: in der Liste stand sie
 *  als „❓ Kein Schritt“, der Klick endete im roten Fehlerbalken). Fehlt das Feld ganz (alte Server-Fassung), gilt
 *  sie nicht als im Bau — geraten wird nicht. */
export function imBau(b: OberthemaBuehne): boolean {
  return 'rezept' in b && (!b.rezept || !Array.isArray(b.rezept.kacheln) || b.rezept.kacheln.length === 0);
}
/** Never infer preparation from a question or a technical phase in the browser. */
export function statusVon(b: OberthemaBuehne): Status {
  switch (b.hauptstatus) {
    case 'fuer_dich_vorbereitet': return 'wartet';
    case 'von_dir_zurueckgestellt': return 'pausiert';
    case 'abgeschlossen': return 'abgeschlossen';
    default: return 'arbeitet';
  }
}
export function aktuellerStatusfilter(filter: Status | null): Status | null {
  if (filter === null) return null;
  return filter === 'ergebnis' ? 'wartet' : STATUS_REIHENFOLGE.includes(filter) ? filter : 'arbeitet';
}
export function statusZaehler(bs: OberthemaBuehne[]): Record<Status, number> & { alle: number } {
  const z = { wartet: 0, anderes: 0, arbeitet: 0, still: 0, unbekannt: 0, pausiert: 0, ergebnis: 0, ohneSchritt: 0, imBau: 0, abgeschlossen: 0, alle: bs.length };
  for (const b of bs) z[statusVon(b)] += 1;
  return z;
}
export function filtereNachStatus<T extends OberthemaBuehne>(bs: T[], filter: Status | null): T[] {
  const aktuell = aktuellerStatusfilter(filter);
  return aktuell === null ? bs : bs.filter(b => statusVon(b) === aktuell);
}

export interface PausenGrundQuelle {
  arbeitsplan?: { abhaengigkeiten?: Array<{ wartetAuf: string; erledigt: boolean }> } | null;
  /** Bereits formulierter Wortlaut des serverseitig berechneten Wartens (`ruhend`), z. B. „wartet auf extern:… bis 16.10.“. */
  ruhendWort?: string | null;
  verlauf?: Array<{ was: string; grund: string; von?: string }> | null;
  standText?: string | null;
}
export interface PausenGrund { text: string; quelle: 'abhaengigkeit' | 'ruhend' | 'verlauf' | 'standtext' | 'keiner' }

/** Der Standardtext des Knopfes „Auf später“ (und Aehnliches) erklaert WER pausiert hat, nicht WARUM — er zaehlt nicht als Grund. */
const STANDARDGRUND_PAUSE = /ausdrücklich in den Pausenraum verschoben|ausdruecklich in den Pausenraum verschoben/i;

/** Erste Satzgruppe eines Freitextes, ohne Etikett wie „Stand:“, auf `max` Zeichen gekuerzt. */
export function ersteSatzgruppe(text: string, max = 70): string {
  const roh = text.replace(/^\s*(?:letzter\s+)?stand\s*:\s*/i, '').trim();
  const satz = roh.split(/(?<=[.!?])\s|\s[—–]\s|\n/)[0]?.trim() ?? '';
  return satz.length > max ? `${satz.slice(0, max - 1).trimEnd()}…` : satz;
}

/**
 * WARUM eine Buehne pausiert ist — aus einem ECHTEN Feld, nichts erfunden. Rangfolge: (1) unerledigte Abhaengigkeit des Arbeitsplans (strukturiert),
 * (2) das serverseitig berechnete Warten `ruhend` (strukturiert), (3) Grund des Raumwechsels in den Pausenraum im Verlauf — aber nur, wenn er
 * mehr sagt als den Standardtext des Knopfes, (4) die erste Satzgruppe des Standtextes (FREITEXT, Quelle „standtext“), sonst „Grund nicht erfasst“.
 */
export function pausenGrund(q: PausenGrundQuelle, max = 70): PausenGrund {
  const offen = q.arbeitsplan?.abhaengigkeiten?.find(a => !a.erledigt);
  if (offen) return { text: `wartet auf ${ersteSatzgruppe(offen.wartetAuf, max)}`, quelle: 'abhaengigkeit' };
  if (q.ruhendWort) return { text: q.ruhendWort.length > max ? `${q.ruhendWort.slice(0, max - 1).trimEnd()}…` : q.ruhendWort, quelle: 'ruhend' };
  const eintrag = [...(q.verlauf ?? [])].reverse().find(v => v.was.endsWith('-> pausenraum') && v.grund.trim() && !STANDARDGRUND_PAUSE.test(v.grund));
  if (eintrag) return { text: ersteSatzgruppe(eintrag.grund, max), quelle: 'verlauf' };
  const stand = q.standText ? ersteSatzgruppe(q.standText, max) : '';
  if (stand) return { text: stand, quelle: 'standtext' };
  return { text: 'Grund nicht erfasst', quelle: 'keiner' };
}

/** Kurzer Tab-Name (Rafael, 24.10.2026, Panel ca. 740 px breit): am Wortende gekuerzt, voller Name im Tooltip. „Energy fertig und verkaufbar“ -> „Energy fertig…“. */
export function tabKurzname(name: string, max = 18): string {
  if (name.length <= max) return name;
  const kopf = name.slice(0, max - 1);
  const leer = kopf.lastIndexOf(' ');
  return `${(leer >= 8 ? kopf.slice(0, leer) : kopf).trimEnd()}…`;
}

/** Kurze Statuswoerter fuer die Zeilenmarke (feste Breite, nie abgeschnitten): „Ergebnis bereit“ heisst in der Zeile „Ergebnis“; die Filterleiste behaelt die Langform. */
export const STATUS_ZEILE_WORT = STATUS_WORT;

export interface WartetGrundQuelle { offeneFragen?: string[] }
/** Der Titel einer echten offenen Frage erklaert den Nutzerbedarf. Keine Ableitung aus alten Aktionsfeldern. */
export function wartetGrund(q: WartetGrundQuelle, max = 140): { text: string; quelle: 'frage' } | null {
  const frage = q.offeneFragen?.map(f => f.trim()).find(Boolean);
  return frage ? { text: `Frage: ${ersteSatzgruppe(frage, max)}`, quelle: 'frage' } : null;
}

// ── Schnellweg: Ansicht per Knopf oder Kurzbefehl setzen — ohne Modell (Rafael, 24.10.2026: „er muss schnell sein“) ───────────────────────────────────────────
/**
 * Ob die Hofansicht im Guide-Panel versteckt wird: nur solange der Verlauf oder eine offene Buehne ihren Platz einnimmt. Der Assistent-Modus versteckt sie NICHT mehr —
 * bis 24.10.2026 tat er es und zeigte stattdessen einen Textkasten, weshalb Rafael oben nie „die gleiche Ansicht“ sah (und der Schnellweg ins Leere griff).
 */
export function hofansichtVersteckt(z: { historieOffen: boolean; offeneBuehne: boolean }): boolean { return z.historieOffen || z.offeneBuehne; }

export type AnsichtArt = 'raum' | 'oberthema';
/** Was ein Befehl an der Ziele-Ansicht aendert; fehlende Felder bleiben unveraendert. */
export interface AnsichtWert { auswahl?: Auswahl; filter?: Status | null; ansicht?: AnsichtArt }
export interface ZielUnterziel { schluessel: string; name: string; kuerzel: string | null }
export interface ZielEintrag { schluessel: string; name: string; kurz: string; anzahl: number; unterziele: ZielUnterziel[] }

/** Die Oberthemen und Unterziele, die ein Kurzbefehl treffen kann — nur Namen und Schluessel, keine Buehnendaten. */
export function zieleAusGruppen(gruppen: Oberthemagruppe[]): ZielEintrag[] {
  return gruppen.map(g => ({
    schluessel: g.schluessel, name: g.name, kurz: tabKurzname(g.name, 14), anzahl: g.anzahl,
    unterziele: g.unterziele.filter(u => u.name !== 'ohne Unterziel').map(u => ({ schluessel: u.schluessel, name: u.name, kuerzel: unterzielKurz(u.name).kuerzel })),
  }));
}

export type AnsichtBefehl =
  | { art: 'befehl'; wert: AnsichtWert; beschreibung: string }
  | { art: 'mehrdeutig'; kandidaten: string[] }
  | { art: 'keiner' };

const normiere = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/ß/g, 'ss').replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
const BEFEHLS_VORSATZ = ['zeige mir', 'zeig mir', 'zeige', 'zeig', 'offne mir', 'offne', 'gehe zu', 'geh zu', 'filter', 'nur', 'reiter', 'ziel'];
const STATUS_PHRASEN: Array<[string, Status]> = [
  ['wartet auf dich', 'wartet'], ['wartet', 'wartet'], ['wartend', 'wartet'],
  ['arbeitet', 'arbeitet'], ['in arbeit', 'arbeitet'],
  ['pausiert', 'pausiert'], ['pausen', 'pausiert'], ['pause', 'pausiert'], ['zuruckgestellt', 'pausiert'],
  ['ergebnis bereit', 'ergebnis'], ['ergebnisse', 'ergebnis'], ['ergebnis', 'ergebnis'], ['fertig', 'ergebnis'],
].sort((a, b) => b[0].length - a[0].length) as Array<[string, Status]>;

/** Die einzigen Statusbegriffe, die auch OHNE Vorsatzwort ein Befehl sind (nicht „fertig“, „pause“, „ergebnis“, „wartet“ allein). */
const STATUS_OHNE_VORSATZ = ['pausiert', 'wartet auf dich', 'arbeitet', 'ergebnis bereit'];
type Treffer = { art: 'ziel'; ziel: ZielEintrag } | { art: 'unterziel'; ziel: ZielEintrag; u: ZielUnterziel } | { art: 'mehrdeutig'; kandidaten: string[] } | { art: 'keiner' };
function sucheZiel(p: string, ziele: ZielEintrag[], streng: boolean): Treffer {
  if (/^[a-z]\d{1,3}$/.test(p)) {
    const t = ziele.flatMap(z => z.unterziele.filter(u => u.kuerzel?.toLowerCase() === p).map(u => ({ ziel: z, u })));
    if (t.length === 1) return { art: 'unterziel', ...t[0] };
    return t.length ? { art: 'mehrdeutig', kandidaten: t.map(x => `${x.u.kuerzel} (${x.ziel.name})`) } : { art: 'keiner' };
  }
  if (p.length < 3) return { art: 'keiner' };
  /* Ohne Vorsatzwort zaehlt nur der exakte volle Name MEHRERER Woerter; Teilwoerter („geld“, „kunden“) und Einwort-Namen („Privat“, „Mail“, „Steuerung“) sind echte Nachrichten (Rafael, 24.10.2026: „zeig privat“ geht, „privat“ allein geht an den Assistenten). */
  const gruppiert = ziele.map(z => ({ z, n: normiere(z.name) }));
  const stufen: Array<(n: string) => boolean> = streng ? [n => n === p && n.includes(' ')] : [n => n === p, n => n.startsWith(`${p} `), n => (` ${n}`).includes(` ${p}`)];
  for (const passt of stufen) {
    const t = gruppiert.filter(x => passt(x.n));
    if (t.length === 1) return { art: 'ziel', ziel: t[0].z };
    if (t.length > 1) return { art: 'mehrdeutig', kandidaten: t.map(x => x.z.name) };
  }
  return { art: 'keiner' };
}

/**
 * Kurzbefehl fuer die Ziele-Ansicht, rein und ohne Modell. Nur ein GANZER Text, der ein Befehl ist, zaehlt; jeder andere Text — auch einer, der „energy“ nur enthaelt — ist
 * `keiner` und geht unveraendert ans Modell. Mehrdeutig (mehrere Oberthemen oder gleiche Kuerzel) ist ebenfalls kein Befehl: es wird nicht geraten.
 *
 * NACHRICHTENVERLUST VERHINDERN (Pruefung 24.10.2026): Einwort-Nachrichten wie „geld“, „kunden“, „fertig“, „pause“, „alle“ koennen echte Anfragen oder Diktat sein.
 * OHNE Vorsatzwort (zeig/oeffne/filter/nur/ziel …) gilt deshalb NUR: (1) ein exakter Statusbegriff („pausiert“, „wartet auf dich“, „arbeitet“, „ergebnis bereit“),
 * (2) ein Unterziel-Kuerzel („V4“, „E3“), (3) der exakte volle Name eines Oberthemas mit MEHREREN Woertern (ein Einwort-Name wie „Privat“, „Mail“ oder „Steuerung“ braucht das Vorsatzwort), (4) „alle ziele“. MIT Vorsatzwort („zeig energy“, „zeig alle“, „nur fertig“,
 * „zeig kunden pausiert“) auch Teilwoerter, Kurzformen, Kombinationen, „raeume“ und „ziele“.
 */
export function parseAnsichtBefehl(text: string, ziele: ZielEintrag[]): AnsichtBefehl {
  let p = normiere(text);
  let vorsatz = false;
  for (const v of BEFEHLS_VORSATZ) if (p.startsWith(`${v} `)) { p = p.slice(v.length + 1).trim(); vorsatz = true; break; }
  if (!p || p.split(' ').length > 6) return { art: 'keiner' };
  const streng = !vorsatz;
  const fertig = (wert: AnsichtWert, teile: string[]): AnsichtBefehl => ({ art: 'befehl', wert, beschreibung: teile.join(' · ') });
  if (p === 'alle ziele' || (vorsatz && p === 'alle')) return fertig({ auswahl: ALLE_AUSWAHL, filter: null, ansicht: 'oberthema' }, ['Alle']);
  if (vorsatz && (p === 'raume' || p === 'raum' || p === 'raume bisher')) return fertig({ ansicht: 'raum' }, ['Räume (bisher)']);
  if (vorsatz && (p === 'ziele' || p === 'ziele neu')) return fertig({ ansicht: 'oberthema' }, ['Ziele (neu)']);
  const nurStatus = STATUS_PHRASEN.find(([w]) => w === p && (vorsatz || STATUS_OHNE_VORSATZ.includes(w)));
  if (nurStatus) return fertig({ filter: nurStatus[1], ansicht: 'oberthema' }, [STATUS_FILTER_WORT[nurStatus[1]]]);
  const zielWert = (t: Treffer): { wert: AnsichtWert; name: string } | null => t.art === 'ziel' ? { wert: { auswahl: { gruppe: t.ziel.schluessel, unterziel: null } }, name: t.ziel.name }
    : t.art === 'unterziel' ? { wert: { auswahl: { gruppe: t.ziel.schluessel, unterziel: t.u.schluessel } }, name: t.u.kuerzel ?? t.u.name } : null;
  const suffix = vorsatz ? STATUS_PHRASEN.find(([w]) => p.endsWith(` ${w}`)) : undefined;
  if (suffix) {
    const rest = p.slice(0, p.length - suffix[0].length - 1).trim();
    const t = sucheZiel(rest, ziele, streng);
    if (t.art === 'mehrdeutig') return { art: 'mehrdeutig', kandidaten: t.kandidaten };
    const z = zielWert(t);
    if (z) return fertig({ ...z.wert, filter: suffix[1], ansicht: 'oberthema' }, [z.name, STATUS_FILTER_WORT[suffix[1]]]);
  }
  const t = sucheZiel(p, ziele, streng);
  if (t.art === 'mehrdeutig') return { art: 'mehrdeutig', kandidaten: t.kandidaten };
  const z = zielWert(t);
  return z ? fertig({ ...z.wert, ansicht: 'oberthema' }, [z.name]) : { art: 'keiner' };
}

export interface SchnellKnopf { id: string; label: string; titel: string; pressed: boolean; wert: AnsichtWert; beschreibung: string }
/**
 * Die Schnellknoepfe im Dock: Alle, Wartet auf dich, Pausiert und die ersten Oberthemen. Ein gedrueckter Knopf, nochmal gedrueckt, nimmt seine Wirkung zurueck
 * (Statusfilter aus, Oberthema auf „Alle“). Jeder Knopf schaltet bewusst auf „Ziele (neu)“ — die Filter gibt es nur dort.
 */
export function schnellKnoepfe(ziele: ZielEintrag[], auswahl: Auswahl, filter: Status | null, max = 6): SchnellKnopf[] {
  const status = (s: Status, label: string): SchnellKnopf => ({ id: `status-${s}`, label, titel: `Nur Bühnen mit Status „${STATUS_FILTER_WORT[s]}“`, pressed: filter === s,
    wert: { filter: filter === s ? null : s, ansicht: 'oberthema' }, beschreibung: filter === s ? 'Alle Status' : STATUS_FILTER_WORT[s] });
  return [
    { id: 'alle', label: 'Alle', titel: 'Alle Oberthemen, alle Status', pressed: auswahl.gruppe === null && filter === null, wert: { auswahl: ALLE_AUSWAHL, filter: null, ansicht: 'oberthema' }, beschreibung: 'Alle' },
    ...STATUS_REIHENFOLGE.map(s=>status(s,`${STATUS_SYMBOL[s]} ${STATUS_WORT[s]}`)),
    ...ziele.slice(0, max).map((z): SchnellKnopf => ({ id: `ziel-${z.schluessel}`, label: z.kurz, titel: z.name, pressed: auswahl.gruppe === z.schluessel,
      wert: { auswahl: auswahl.gruppe === z.schluessel ? ALLE_AUSWAHL : { gruppe: z.schluessel, unterziel: null }, ansicht: 'oberthema' }, beschreibung: auswahl.gruppe === z.schluessel ? 'Alle' : z.name })),
  ];
}
